import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { ShoppingCart, User, Package, LogOut } from 'lucide-react';

const BuyerDashboard = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Appliquer la couleur de fond pour les buyers
    document.body.style.backgroundColor = '#f8f9fa';
    
    const fetchUserData = async () => {
      try {
        const user = auth.currentUser;
        
        if (!user) {
          navigate('/farmer-login');
          return;
        }

        const userDoc = await getDoc(doc(db, 'buyers', user.uid));
        
        if (!userDoc.exists()) {
          setError('Données utilisateur non trouvées');
          return;
        }
        
        setUserData(userDoc.data());
      } catch (err) {
        console.error('Erreur lors de la récupération des données:', err);
        setError('Erreur lors de la récupération des données');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
    
    // Nettoyer la couleur de fond lors du démontage du composant
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" component="h1" gutterBottom>
            Tableau de bord Acheteur
          </Typography>
          <Button 
            variant="outlined" 
            color="error" 
            startIcon={<LogOut size={18} />}
            onClick={handleLogout}
          >
            Déconnexion
          </Button>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        {userData && (
          <Box mb={4}>
            <Typography variant="h6" gutterBottom>
              Bienvenue, {userData.firstName} {userData.lastName}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {userData.email}
            </Typography>
          </Box>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <User size={24} color="#014527" />
                  <Typography variant="h6" ml={1}>Mon Profil</Typography>
                </Box>
                <Typography variant="body2">
                  Consultez et modifiez vos informations personnelles
                </Typography>
                <Button 
                  variant="text" 
                  color="primary" 
                  sx={{ mt: 2 }}
                  // onClick={() => navigate('/buyer-profile')}
                >
                  Voir mon profil
                </Button>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <ShoppingCart size={24} color="#014527" />
                  <Typography variant="h6" ml={1}>Mes Commandes</Typography>
                </Box>
                <Typography variant="body2">
                  Consultez l'historique de vos commandes
                </Typography>
                <Button 
                  variant="text" 
                  color="primary" 
                  sx={{ mt: 2 }}
                  // onClick={() => navigate('/buyer-orders')}
                >
                  Voir mes commandes
                </Button>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <Package size={24} color="#014527" />
                  <Typography variant="h6" ml={1}>Produits Disponibles</Typography>
                </Box>
                <Typography variant="body2">
                  Découvrez les produits proposés par nos agriculteurs
                </Typography>
                <Button 
                  variant="text" 
                  color="primary" 
                  sx={{ mt: 2 }}
                  // onClick={() => navigate('/products')}
                >
                  Voir les produits
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default BuyerDashboard;